import { useState, useEffect } from "react";
import {
  Button,
  useColorMode,
  Flex,
  Heading,
  Spacer,
  Container,
  FormControl,
  FormLabel,
  InputGroup,
  Input,
  Center,
  useToast,
  Image,
  Box,
  Link,
  Text,
  VStack,
  InputRightElement,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { SunIcon, MoonIcon } from "@chakra-ui/icons";
import ConnectWallet from "./components/ConnectWallet";
import { Contract, ethers } from "ethers";
import { Web3Provider, Signer } from "./types";
import Footer from "./components/Footer";

function App() {
  const { colorMode, toggleColorMode } = useColorMode();
  const underlineColor = { light: "gray.500", dark: "gray.400" };
  const bgColor = { light: "white", dark: "gray.700" };
  const toast = useToast();

  const [provider, setProvider] = useState<Web3Provider>();
  const [signer, setSigner] = useState<Signer>();
  const [id, setId] = useState<number>(1);

  const [contract, setContract] = useState<Contract>();
  const [loading, setLoading] = useState<boolean>(false);
  const [image, setImage] = useState<string>();
  const [owner, setOwner] = useState<string>("");
  const [totalSupply, setTotalSupply] = useState<string>("");
  const [totalSupplyLoading, setTotalSupplyLoading] = useState<boolean>(false);

  const pilotAddress = "0x3E5b1Be24334705199b3AdbBF715A593F449255c";
  const pilotABI = require("./abi/Pilot.json");

  useEffect(() => {
    if (provider) {
      setContract(new ethers.Contract(pilotAddress, pilotABI, provider));
      setSigner(provider.getSigner(0));
    }
  }, [provider]);

  useEffect(() => {
    if (contract) {
      fetchTotalSupply();
      fetchImage(id);
    }
  }, [contract]);

  const fetchImage = async (_id: number = id) => {
    setLoading(true);

    try {
      const tokenURI = await contract!.tokenURI(_id);
      // 29 = length of "data:application/json;base64,"
      const jsonString = Buffer.from(tokenURI.substring(29), "base64").toString(
        "binary"
      );
      const json = JSON.parse(jsonString);
      console.log(json);
      setImage(json.image);
      let _owner;
      try {
        _owner = await contract!.ownerOf(_id);
      } catch {
        _owner = ethers.constants.AddressZero;
      }
      setOwner(_owner);
    } catch {}
    setLoading(false);
  };

  const fetchTotalSupply = async () => {
    setTotalSupplyLoading(true);
    setTotalSupply((await contract!.totalSupply()).toString());
    setTotalSupplyLoading(false);
  };

  function randomIntFromInterval(min: number, max: number) {
    // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  return (
    <>
      <Flex
        py="4"
        px={["2", "4", "10", "10"]}
        borderBottom="2px"
        borderBottomColor={underlineColor[colorMode]}
      >
        <Spacer flex="1" />
        <Heading maxW={["302px", "4xl", "4xl", "4xl"]}>
          Pilot Explorer 🔍
        </Heading>
        <Flex flex="1" justifyContent="flex-end">
          <Button onClick={toggleColorMode} rounded="full" h="40px" w="40px">
            {colorMode === "light" ? <MoonIcon /> : <SunIcon />}
          </Button>
        </Flex>
      </Flex>
      <Container my="16" minH="md" minW={["0", "0", "2xl", "2xl"]}>
        {provider && (
          <Center>
            <Text>
              Total Supply: {totalSupplyLoading ? "..." : totalSupply}{" "}
            </Text>
            <Button ml="4" size="sm" onClick={() => fetchTotalSupply()}>
              🔄️
            </Button>
          </Center>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetchImage();
          }}
        >
          <FormControl>
            <FormLabel>Enter tokenId to view its image</FormLabel>
            <InputGroup>
              <Input
                placeholder="tokenId"
                aria-label="tokenId"
                type="number"
                autoComplete="off"
                value={id}
                onChange={(e) => {
                  setId(parseInt(e.target.value));
                }}
                bg={bgColor[colorMode]}
                isDisabled={!provider}
              />
              {provider && (
                <InputRightElement width="4.5rem" mr="1rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => {
                      const _random = randomIntFromInterval(1, 7777);
                      setId(_random);
                      fetchImage(_random);
                    }}
                  >
                    Random
                  </Button>
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
          {provider && (
            <Center>
              {id > 1 && (
                <Button
                  isLoading={loading}
                  mt="1rem"
                  onClick={() => {
                    const newId = id - 1;
                    setId(newId);
                    fetchImage(newId);
                  }}
                >
                  Prev
                </Button>
              )}
              <Box pl="1rem">
                <Button
                  isLoading={loading}
                  pl="1rem"
                  mt="1rem"
                  onClick={() => {
                    const newId = id + 1;
                    setId(newId);
                    fetchImage(newId);
                  }}
                >
                  Next
                </Button>
              </Box>
            </Center>
          )}
        </form>
        <Center>
          {provider ? (
            <VStack mt="1rem">
              {owner && (
                <>
                  <Text>
                    Owner:{" "}
                    <Link
                      href={`https://etherscan.io/address/${owner}`}
                      isExternal
                    >
                      {owner} <ExternalLinkIcon />
                    </Link>
                  </Text>
                  {owner === ethers.constants.AddressZero && (
                    <Button
                      onClick={() => {
                        try {
                          contract!.connect(signer!).claim(id);
                        } catch {
                          toast({
                            title: "Txn error",
                            status: "error",
                            isClosable: true,
                            duration: 4000,
                          });
                        }
                      }}
                    >
                      Mint!
                    </Button>
                  )}
                </>
              )}
              <Image py="0.5rem" w="30rem" src={image} />
              <Text>
                Pilot Contract:{" "}
                <Link
                  href={`https://etherscan.io/address/${pilotAddress}`}
                  isExternal
                >
                  {pilotAddress} <ExternalLinkIcon />
                </Link>
              </Text>
            </VStack>
          ) : (
            <ConnectWallet mt="10rem" setProvider={setProvider} />
          )}
        </Center>
      </Container>
      <Footer />
    </>
  );
}

export default App;
